import { ADVENTURE_REWARD_ICONS, type AdventureBoxReward } from "../../adventure/adventure_box_rewards.js";
import { rewardAmountText } from "../../common/reward_format.js";
import { ImgID } from "../../image_loader/img_id.js";
import {
  isPlaceBoxItem,
  TournamentService,
  type TournamentBox,
  type TournamentPlaceBoxItem,
  type TournamentPlaceItem,
  type TournamentStanding,
} from "../../tournament/tournament_service.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIAvatarSelectionItem } from "../common_ui_elements/ui_avatar_selection_item.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";

const BOX_IMAGES: Record<TournamentBox, ImgID> = {
  green: ImgID.UI_TournamentBoxGreen,
  blue: ImgID.UI_TournamentBoxBlue,
  red: ImgID.UI_TournamentBoxRed,
  purple: ImgID.UI_TournamentBoxPurple,
};

const ROW_ART = { w: 130, left: 12, right: 116 };
const BOX_ART = { w: 460, h: 440, left: 17, top: 99, right: 429, bottom: 411 };
const CROWNS_ART = { w: 148, h: 123, left: 4, top: 1, right: 143, bottom: 115, body_top: 57 };
const STAR_IMAGES = [ ImgID.UI_TournamentStarFirst, ImgID.UI_TournamentStarSecond, ImgID.UI_TournamentStarThird ];
const STAR_RANK_OUTLINES = [
  ColorScheme.tournament_star_rank_outline_first,
  ColorScheme.tournament_star_rank_outline_second,
  ColorScheme.tournament_star_rank_outline_third,
];

/** A child's rectangle in row-local pixels. */
export interface RowBounds
{
  x: number;
  y: number;
  w: number;
  h: number;
}

interface PrizeSlot
{
  item: TournamentPlaceItem;
  element: UIImage;
}

export class TournamentStandingsRow extends UIElement
{
  private static readonly TOP_RANKS = STAR_IMAGES.length;
  private static readonly LEFT_CONTENT_OFFSET = 10;
  private static readonly AVATAR_X = 68;
  private static readonly AVATAR_SIZE = 118;
  private static readonly AVATAR_BORDER = 4;
  private static readonly RANK_FONT = 40;
  private static readonly NAME_GAP = 20;
  private static readonly NAME_H = 60;
  private static readonly CROWNS_RIGHT_MARGIN = 26;
  private static readonly ITEMS_CROWNS_GAP = 10;
  private static readonly ITEM_COL_W = 104;
  private static readonly ITEM_GAP = 8;
  private static readonly BOX_W = 116;
  private static readonly BOX_H = 108;
  // The shop's coin pile is 5:4.
  private static readonly COINS_W = 104;
  private static readonly COINS_H = 83;
  private static readonly COINS_Y = 14;
  private static readonly ICON_SIZE = 80;
  private static readonly ICON_Y = 17;
  private static readonly AMOUNT_TEXT_H = 52;
  private static readonly AMOUNT_TEXT_FONT = 38;
  private static readonly AMOUNT_TEXT_OUTLINE = 8;
  private static readonly CROWNS_W = 140;
  private static readonly CROWNS_H = 113;
  private static readonly CROWN_SIZE = 52;
  private static readonly CROWN_Y = 5;
  private static readonly CROWNS_TEXT_Y_OFFSET = 4;
  private static readonly CROWNS_TEXT_FONT = 37;
  private static readonly STAR_W = 86;
  private static readonly STAR_H = 80;
  private static readonly STAR_CENTER_X = 32;
  private static readonly STAR_TILT_DEG = -15;
  private static readonly STAR_RANK_OUTLINE_WIDTH = 6;

  private readonly standing: TournamentStanding;
  private readonly items: TournamentPlaceItem[];
  private crowns_badge!: UIImage;
  private item_slots: PrizeSlot[] = [];

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, standing: TournamentStanding )
  {
    super( ctx, x, y, width, height );
    this.standing = standing;
    this.items = TournamentService.getPlaceItemsFor( standing.rank );

    this.createBackground();
    if ( this.isTopRank() )
      this.createStar();
    this.createRank();
    this.createAvatar();
    this.createName();
    this.createItems();
    this.createCrowns();
  }

  public name(): string { return "TournamentStandingsRow"; }

  public crownsBounds(): RowBounds
  {
    return TournamentStandingsRow.boundsOf( this.crowns_badge );
  }

  public prizeSlots(): { item: TournamentPlaceItem; bounds: RowBounds }[]
  {
    return this.item_slots.map( slot => ( { item: slot.item, bounds: TournamentStandingsRow.boundsOf( slot.element ) } ) );
  }

  private static boundsOf( element: UIElement ): RowBounds
  {
    const pos = element.pixelPosImportant();
    const rect = element.getRect();
    return { x: pos.x, y: pos.y, w: rect.getW(), h: rect.getH() };
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  private isTopRank(): boolean
  {
    return this.standing.rank <= TournamentStandingsRow.TOP_RANKS;
  }
  public getRank(): number
  {
    return this.standing.rank;
  }


  private centerY( h: number ): number
  {
    return ( this.rect.getH() - h ) / 2;
  }

  private createBackground(): void
  {
    const img_id = this.standing.is_player ? ImgID.UI_TournamentItemPlayerBg
      : this.isTopRank() ? ImgID.UI_TournamentItemTopBg
        : ImgID.UI_TournamentItemBg;

    this.addChild( new UINineSliceElement( this.ctx, 0, 0, this.rect.getW(), this.rect.getH(), img_id, "tournament_item" ) );
  }

  private createStar(): void
  {
    const bounds = this.starBounds();

    const star = new UIImage(
      this.ctx,
      bounds.x,
      bounds.y,
      bounds.w,
      bounds.h,
      STAR_IMAGES[this.standing.rank - 1]
    );
    star.enableRotation();
    star.setRotationImmediate( TournamentStandingsRow.STAR_TILT_DEG * Math.PI / 180 );
    star.setClickable( false );
    this.addChild( star );
  }

  private starBounds(): RowBounds
  {
    const w = LayoutScaler.widthFromDesign( TournamentStandingsRow.STAR_W );
    const h = LayoutScaler.heightFromDesign( TournamentStandingsRow.STAR_H );
    return {
      x: LayoutScaler.xFromDesign( TournamentStandingsRow.STAR_CENTER_X + TournamentStandingsRow.LEFT_CONTENT_OFFSET ) - w / 2,
      y: this.centerY( h ),
      w,
      h,
    };
  }

  private createRank(): void
  {
    const top = this.isTopRank();

    const star = top ? this.starBounds() : null;
    const rank_x = star
      ? star.x
      : LayoutScaler.xFromDesign( ROW_ART.left + TournamentStandingsRow.LEFT_CONTENT_OFFSET - 5 );
    const rank_y = star ? star.y : 0;
    const rank_w = star?.w ?? LayoutScaler.widthFromDesign( TournamentStandingsRow.AVATAR_X - ROW_ART.left );
    const rank_h = star?.h ?? this.rect.getH();

    this.addChild( new UITextElement(
      this.ctx,
      rank_x,
      rank_y,
      rank_w,
      rank_h,
      {
        text: String( this.standing.rank ),
        font_size: LayoutScaler.textSizeFromDesign( TournamentStandingsRow.RANK_FONT ),
        font_weight: 900,
        text_color: top ? ColorScheme.white : ColorScheme.tournament_row_text,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( star ? TournamentStandingsRow.STAR_RANK_OUTLINE_WIDTH : 8 ),
        outline_color: star ? STAR_RANK_OUTLINES[this.standing.rank - 1]
          : top ? ColorScheme.tournament_row_top_rank_outline : ColorScheme.white,
        // The canvas path fills outlined text with the default 2px hard shadow; no shadow here.
        shadow_color: "rgba(0, 0, 0, 0)",
      }
    ) );
  }

  private createAvatar(): void
  {
    const size = LayoutScaler.widthFromDesign( TournamentStandingsRow.AVATAR_SIZE );

    const avatar = new UIAvatarSelectionItem(
      this.ctx,
      LayoutScaler.xFromDesign( TournamentStandingsRow.AVATAR_X + TournamentStandingsRow.LEFT_CONTENT_OFFSET ),
      this.centerY( size ),
      size,
      size,
      this.standing.avatar_id,
      () => {},
      LayoutScaler.widthFromDesign( TournamentStandingsRow.AVATAR_BORDER ),
      0,
      undefined,
      false,
      ColorScheme.white
    );
    avatar.setClickable( false );
    this.addChild( avatar );
  }

  private crownsVisibleLeft(): number
  {
    const visible_w = LayoutScaler.widthFromDesign(
      TournamentStandingsRow.CROWNS_W * ( CROWNS_ART.right - CROWNS_ART.left ) / CROWNS_ART.w );
    return this.rect.getW() - LayoutScaler.widthFromDesign( TournamentStandingsRow.CROWNS_RIGHT_MARGIN ) - visible_w;
  }

  private itemColumnLeft( from_right: number ): number
  {
    const col_w = LayoutScaler.widthFromDesign( TournamentStandingsRow.ITEM_COL_W );
    const gap = LayoutScaler.widthFromDesign( TournamentStandingsRow.ITEM_GAP );
    const right = this.crownsVisibleLeft() - LayoutScaler.widthFromDesign( TournamentStandingsRow.ITEMS_CROWNS_GAP );
    return right - ( from_right + 1 ) * col_w - from_right * gap;
  }

  private itemColumnCenter( index: number ): number
  {
    const col_w = LayoutScaler.widthFromDesign( TournamentStandingsRow.ITEM_COL_W );
    return this.itemColumnLeft( this.items.length - 1 - index ) + col_w / 2;
  }

  private rightBlockLeft(): number
  {
    if ( this.items.length > 0 ) return this.itemColumnLeft( this.items.length - 1 );
    return this.crownsVisibleLeft();
  }

  private createName(): void
  {
    const left = LayoutScaler.xFromDesign( TournamentStandingsRow.AVATAR_X + TournamentStandingsRow.LEFT_CONTENT_OFFSET
      + TournamentStandingsRow.AVATAR_SIZE + TournamentStandingsRow.NAME_GAP );
    const right = this.rightBlockLeft() - LayoutScaler.widthFromDesign( TournamentStandingsRow.NAME_GAP );
    const h = LayoutScaler.heightFromDesign( TournamentStandingsRow.NAME_H );

    this.addChild( new UITextElement(
      this.ctx,
      left,
      this.centerY( h ),
      right - left,
      h,
      {
        text: this.standing.name,
        font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_color: ColorScheme.tournament_row_text,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
      }
    ) );
  }

  private createItems(): void
  {
    this.items.forEach( ( item, index ) =>
    {
      const center_x = this.itemColumnCenter( index );
      const element = isPlaceBoxItem( item ) ? this.createBox( item, center_x ) : this.createReward( item, center_x );
      this.item_slots.push( { item, element } );
    } );
  }

  private createReward( reward: AdventureBoxReward, center_x: number ): UIImage
  {
    const is_coin = reward.type === "coin";
    const w = LayoutScaler.widthFromDesign( is_coin ? TournamentStandingsRow.COINS_W : TournamentStandingsRow.ICON_SIZE );
    const h = LayoutScaler.heightFromDesign( is_coin ? TournamentStandingsRow.COINS_H : TournamentStandingsRow.ICON_SIZE );
    const x = center_x - w / 2;
    const y = LayoutScaler.yFromDesign( is_coin ? TournamentStandingsRow.COINS_Y : TournamentStandingsRow.ICON_Y );
    const img_id = is_coin ? ImgID.UI_ShopBlockImg : ADVENTURE_REWARD_ICONS[reward.type];

    const icon = new UIImage( this.ctx, x, y, w, h, img_id );
    icon.setClickable( false );

    const text_h = LayoutScaler.heightFromDesign( TournamentStandingsRow.AMOUNT_TEXT_H );
    const text_y = h - text_h / 2;
    const col_w = LayoutScaler.widthFromDesign( TournamentStandingsRow.ITEM_COL_W );
    const text_x = ( w - col_w ) / 2;
    icon.addChild( new UITextElement( this.ctx, text_x, text_y, col_w, text_h, {
      text: is_coin ? String( reward.amount ) : rewardAmountText( reward.type, reward.amount ),
      font_size: LayoutScaler.textSizeFromDesign( TournamentStandingsRow.AMOUNT_TEXT_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      outline_width: LayoutScaler.textBorderSizeFromDesign( TournamentStandingsRow.AMOUNT_TEXT_OUTLINE ),
      outline_color: ColorScheme.booster_dialog_quantity_outline,
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } ), true );
    this.addChild( icon );
    return icon;
  }

  private createBox( item: TournamentPlaceBoxItem, center_x: number ): UIImage
  {
    const w = LayoutScaler.widthFromDesign( TournamentStandingsRow.BOX_W );
    const h = LayoutScaler.heightFromDesign( TournamentStandingsRow.BOX_H );
    const x = center_x - ( BOX_ART.left + BOX_ART.right ) / 2 / BOX_ART.w * w;
    const y = this.rect.getH() / 2 - ( BOX_ART.top + BOX_ART.bottom ) / 2 / BOX_ART.h * h;

    const box = new UIImage( this.ctx, x, y, w, h, BOX_IMAGES[item.box] );
    this.addChild( box );
    return box;
  }

  private createCrowns(): void
  {
    const w = LayoutScaler.widthFromDesign( TournamentStandingsRow.CROWNS_W );
    const h = LayoutScaler.heightFromDesign( TournamentStandingsRow.CROWNS_H );
    const sx = w / CROWNS_ART.w;
    const sy = h / CROWNS_ART.h;

    const visible_w = ( CROWNS_ART.right - CROWNS_ART.left ) * sx;
    const visible_x = this.crownsVisibleLeft();

    const badge = new UIImage(
      this.ctx,
      visible_x - CROWNS_ART.left * sx,
      this.rect.getH() / 2 - ( CROWNS_ART.top + CROWNS_ART.bottom ) / 2 * sy,
      w,
      h,
      this.standing.is_player ? ImgID.UI_TournamentCrownsSubstratePlayer : ImgID.UI_TournamentCrownsSubstrate
    );

    const crown = LayoutScaler.widthFromDesign( TournamentStandingsRow.CROWN_SIZE );
    const visible_center_x = ( CROWNS_ART.left + CROWNS_ART.right ) / 2 * sx;
    badge.addChild( new UIImage(
      this.ctx,
      visible_center_x - crown / 2,
      LayoutScaler.yFromDesign( TournamentStandingsRow.CROWN_Y ),
      crown,
      LayoutScaler.heightFromDesign( TournamentStandingsRow.CROWN_SIZE ),
      ImgID.UI_TournamentMiniCrown
    ), true );

    badge.addChild( new UITextElement(
      this.ctx,
      CROWNS_ART.left * sx,
      CROWNS_ART.body_top * sy + LayoutScaler.yFromDesign( TournamentStandingsRow.CROWNS_TEXT_Y_OFFSET ),
      visible_w,
      ( CROWNS_ART.bottom - CROWNS_ART.body_top ) * sy,
      {
        text: String( this.standing.crowns ),
        font_size: LayoutScaler.textSizeFromDesign( TournamentStandingsRow.CROWNS_TEXT_FONT ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 9 ),
        outline_color: ColorScheme.tournament_crowns_outline,
      }
    ), true );

    this.crowns_badge = badge;
    this.addChild( badge );
  }
}
